import { Component, ElementRef, inject, OnDestroy, OnInit, viewChild } from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ReplaySubject } from 'rxjs';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faRectangleList } from '@fortawesome/free-solid-svg-icons';
import { faSpinner } from '@fortawesome/pro-light-svg-icons';

import { AnamneseFunctionALUploadType, AnamneseUploadType, DefaultAllowedExtensions } from 'libs/media/src/lib/models/media.model';
import { SafeUrlPipe } from "libs/tm-common/src/lib/pipes/safeUrl.pipe"
import {
	CA_WEBVIEWER_APP_URL,
	CA_WEBVIEWER_APP_ORIGIN,
	isWebviewerMessage,
	WebViewerMessage,
	WebViewerMessageInitialized,
	WebViewerMessageType,
	WebViewerMessageLoadingFinished
} from "libs/web-viewer/src/lib/models/web-viewer-app.model";
import { WebViewerDataContainer, WebViewerDataContainerType } from 'libs/web-viewer/src/lib/models/webviewer-data.model';
import { FilePreviewData } from '../../services/file-preview.lib.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';

type WebviewerFileConfig = {
	filename: string;
	jaw: WebviewerJaw;
	type: WebviewerKfoType;
	fileType: string;
	blob: Blob;
}

enum WebviewerJaw {
	UPPER = "upper",
	LOWER = "lower",
	NONE = "none"
}

enum WebviewerKfoType {
	JAW = "jaw",
	OVERLAY = "overlay"
}

enum PreviewFileType {
	IMAGE = "image",
	PDF = "pdf",
	MODEL_3D = "model",
	UNKNOWN = "unknown"
}

@Component({
	selector: 'lib-file-preview-dialog',
	templateUrl: './file-preview-dialog.component.html',
	styleUrl: './file-preview-dialog.component.scss',
	imports: [
		AsyncPipe,
		MatButtonModule,
		MatDialogTitle,
		MatDialogContent,
		MatDialogClose,
		MatDialogActions,
		NgClass,
		TranslatePipe,
		SafeUrlPipe,
		TranslateModule,
		MatTooltipModule,
		FontAwesomeModule,
		MatSidenavModule,
		MatDividerModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		ReactiveFormsModule,
		MatIconModule,
	]
})
export class FilePreviewDialogComponent implements OnInit, OnDestroy {
  	readonly data = inject<FilePreviewData | FilePreviewData[]>(MAT_DIALOG_DATA);

	private translate = inject(TranslateService);
	private sanitizer = inject(DomSanitizer);
	private formBuilder = inject(FormBuilder);

	dataUrl: string; // necessary for image and pdf preview
	previewFileType: PreviewFileType = PreviewFileType.UNKNOWN;

	protected caWebViewerUrl: SafeResourceUrl;
	private readonly caWebViewer = viewChild<ElementRef>('caWebViewer');
	private windowMessageEventHandler: (event: MessageEvent) => void = this.onIframeWindowMessage.bind(this);
	private webviewerInitialized$: ReplaySubject<WebViewerMessageInitialized> =
		new ReplaySubject(1);
	protected webviewerLoadingFinished$: ReplaySubject<WebViewerMessageLoadingFinished> =
		new ReplaySubject(1);

	PreviewFileType = PreviewFileType;
	faRectangleList = faRectangleList;
	faSpinner = faSpinner;

	webviewerSettingsForm: FormGroup;

	WebviewerKfoType = WebviewerKfoType;
	WebviewerJaw = WebviewerJaw;

	ngOnInit(): void {
		window.addEventListener("message", this.windowMessageEventHandler);

		// multiple file preview is only possible for stl files
		if(Array.isArray(this.data)){
			this.initializeFormGroup();
			this.previewFileType = PreviewFileType.MODEL_3D;
			const webviewerInput = this.createWebviewerInputFile();
			this.previewWebviewerFile(webviewerInput);
			return;
		}

		const previewData = this.data;
		this.determineFileType(previewData);
		if (this.previewFileType === PreviewFileType.IMAGE) {
			this.createImageUrlFromBlob(previewData.blob);
		}else if (this.previewFileType === PreviewFileType.PDF) {
			const fixedBlob = new Blob([previewData.blob], { type: 'application/pdf' });
			this.dataUrl = URL.createObjectURL(fixedBlob);
		}else if (this.previewFileType === PreviewFileType.MODEL_3D) {
			const webviewerInput = this.createWebviewerInputFile(previewData);
			this.previewWebviewerFile(webviewerInput);
		}
	}

	ngOnDestroy() {
		if ([PreviewFileType.PDF, PreviewFileType.MODEL_3D].includes(this.previewFileType)) {
			URL.revokeObjectURL(this.dataUrl);
		}
		window.removeEventListener("message", this.windowMessageEventHandler);
	}

	private determineFileType(previewData: FilePreviewData){
		switch(previewData.fileType.toLowerCase()){
			case DefaultAllowedExtensions.PNG:
			case DefaultAllowedExtensions.JPG:
			case DefaultAllowedExtensions.JPEG:
			case 'svg':
				this.previewFileType = PreviewFileType.IMAGE;
				break;
			case DefaultAllowedExtensions.PDF:
				this.previewFileType = PreviewFileType.PDF;
				break;
			case DefaultAllowedExtensions.STL:
			case DefaultAllowedExtensions.PLY:
				this.previewFileType = PreviewFileType.MODEL_3D;
				break;
			default:
				this.previewFileType = PreviewFileType.UNKNOWN;
		}
	}

	private createImageUrlFromBlob(file: Blob) {
		const reader = new FileReader();
		reader.onload = () => {
			this.dataUrl = reader.result as string;
		};
		reader.readAsDataURL(file);
	}

	/**
	 * Creates a webviewer input file
	 * For single file preview, the filePrevieData is necessary
	 * For multiple files, the function uses the values of the formgroup to
	 * create the json file
	 * @returns webviewer input file
	 */
	private createWebviewerInputFile(filePreviewData?: FilePreviewData) : WebViewerDataContainer {
		let models: any[];
		if(filePreviewData){
			const jaw = this.determineJawByUploadType(filePreviewData.uploadType);
			const meta = this.mapJawToModelMetaInfo(jaw);
			models = [{
				src: URL.createObjectURL(filePreviewData.blob),
				meta: meta,
				name: filePreviewData.orgFilename,
				kfo: WebviewerKfoType.JAW,
				type: filePreviewData.fileType?.toLowerCase()
			}];
		}else{
			models = this.formGroupFileConfigs?.value.map((data: WebviewerFileConfig) => {
				const meta = this.mapJawToModelMetaInfo(data.jaw);
				return {
					src: URL.createObjectURL(data.blob),
					meta: meta,
					name: data.filename,
					kfo: data.type,
					type: data.fileType
				};
			});
		}

		return {
			data: {
				models: models
			},
			meta: {
				referenceId: ""
			},
			type: WebViewerDataContainerType.VIEWER,
			version: "2.0.0"
		};
	}

	private previewWebviewerFile(webViewerInput: any){
		// bypass security for caWebViewer app url
		this.caWebViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(CA_WEBVIEWER_APP_URL);

		this.webviewerInitialized$.subscribe(() => {
			const contentWindow = this.caWebViewer()?.nativeElement?.contentWindow;
			if (contentWindow) {
				contentWindow.postMessage(
					webViewerInput,
					CA_WEBVIEWER_APP_ORIGIN
				);
			}
		});
	}


	private initializeFormGroup(): void {
		const fileConfigArray: FormArray = this.formBuilder.array([]);

		// Create form controls for each file
		this.filePreviewDataAsArray.forEach((fileData: FilePreviewData) => {
			const fileConfig: FormGroup = this.formBuilder.group({
				filename: new FormControl(fileData.orgFilename),
				jaw: new FormControl(this.determineJawByUploadType(fileData.uploadType)),
				type: new FormControl(WebviewerKfoType.JAW),
				blob: new FormControl(fileData.blob),
				fileType: new FormControl(fileData.fileType.toLowerCase())
			});
			fileConfigArray.push(fileConfig);
		});

		this.webviewerSettingsForm = this.formBuilder.group({
			fileConfigs: fileConfigArray
		});
	}

	protected updateWebviewerSettings(drawer: MatDrawer): void {
		const webViewerInput = this.createWebviewerInputFile();
		// reset webviewer app
		this.webviewerInitialized$ = new ReplaySubject(1);
		this.webviewerLoadingFinished$ = new ReplaySubject(1);
		this.caWebViewerUrl = "";
		this.previewWebviewerFile(webViewerInput);
		drawer.close();
		this.webviewerSettingsForm.markAsPristine();
	}

	private onIframeWindowMessage(event: MessageEvent<any>): void {
		if (!isWebviewerMessage(event)) {
			return;
		}
		event.stopPropagation();

		const data: WebViewerMessage = event.data;
		if(data.type === WebViewerMessageType.INITIALIZED){
			this.webviewerInitialized$.next(data);
			this.webviewerInitialized$.complete();
			return;
		}

		if(data.type === WebViewerMessageType.LOADING_FINISHED){
			this.webviewerLoadingFinished$.next(data);
			this.webviewerLoadingFinished$.complete();
			return;
		}
	}

	protected getDialogTitle(): string {
		if(Array.isArray(this.data)){
			return this.translate.instant("3D file preview");
		}
		return this.data.orgFilename;
	}

	protected getPreviewedFiles(): string | undefined {
		if(Array.isArray(this.data)){
			return this.data.map((data: FilePreviewData) => `- ${data.orgFilename}`).join("\n");
		}
		return undefined;
	}

	private mapJawToModelMetaInfo(webviewerJaw: WebviewerJaw): {jaw?: WebviewerJaw.UPPER | WebviewerJaw.LOWER}{
		if(webviewerJaw === WebviewerJaw.NONE){
			return {};
		}
		return {jaw: webviewerJaw};
	}

	private determineJawByUploadType(
		uploadType: AnamneseFunctionALUploadType | AnamneseUploadType
	): WebviewerJaw {
		if (
			[
				AnamneseUploadType.UJ,
				AnamneseFunctionALUploadType.UJ_BITE,
				AnamneseFunctionALUploadType.UJ_REGISTRATION,
			].includes(uploadType)
		) {
			return WebviewerJaw.UPPER;
		}
		if (
			[
				AnamneseUploadType.LJ,
				AnamneseFunctionALUploadType.LJ_BITE,
				AnamneseFunctionALUploadType.LJ_REGISTRATION,
			].includes(uploadType)
		) {
			return WebviewerJaw.LOWER;
		}
		return WebviewerJaw.NONE;
	}

	protected isMultipleStlPreview(): boolean {
		return Array.isArray(this.data) && this.data.length > 1;
	}


	get formGroupFileConfigs(): FormArray {
		return this.webviewerSettingsForm.get('fileConfigs') as FormArray;
	}

	get filePreviewDataAsArray(): FilePreviewData[] {
		if(Array.isArray(this.data)){
			return this.data;
		}
		return [this.data];
	}
}
